
import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Users, Briefcase, BookOpen, Home, GraduationCap, Presentation, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const MorganProfile = () => {
  const user = {
    name: "Morgan Eldred, PhD",
    role: "Advisor - IM, IP, and IT Law",
    email: "morgan@digitalenergy.ai",
    solutionBounties: 7,
    domains: [
      "Information Management",
      "IT Law",
      "Intellectual Property",
      "Digital Transformation",
      "Strategic Management",
      "Energy Sector",
      "Telecommunications",
      "Business Innovation",
      "Project Management"
    ],
    industries: [
      { name: "Technology", icon: Briefcase },
      { name: "Consulting", icon: Presentation },
      { name: "Education", icon: GraduationCap },
      { name: "Research", icon: BookOpen },
      { name: "Energy", icon: Home }
    ],
    confirmedSolutions: 30,
    confirmedKnowledgeBases: 50,
    tokenBalance: 190,
    bio: "Morgan Eldred is a startup founder and expert in many topics of importance to iSPAI, like Information Management, Law, and Technology. With over 25 years of experience in digital transformation and information management, Morgan has led strategic initiatives across the energy, technology, and telecommunications sectors. As the founder and lead Partner of Digital Energy in Dubai, he has delivered innovative digital strategies, while his leadership roles at Gartner and Maersk Oil focused on guiding industry leaders and transforming information systems for business efficiency.\n\nMorgan's educational background is equally impressive, with a PhD in Computing and an MSc in Strategic Business IT from the University of Portsmouth, an LLM in Information Technology Law from The University of Edinburgh, and an International Executive MBA from IE Business School. He also holds a Certificate in Advanced Project Management from Stanford University. His ability to align technology strategies with business goals makes him a key contributor to iSPAI's mission to democratize knowledge and empower the workforce through scalable technology solutions."
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/0158c940-ce9e-441e-86b2-3481f44f3741.png"
                    imageAlt="Morgan Eldred"
                  >
                    <Avatar className="h-48 w-48 cursor-pointer">
                      <AvatarImage 
                        src="/lovable-uploads/0158c940-ce9e-441e-86b2-3481f44f3741.png" 
                        alt="Morgan Eldred"
                      />
                      <AvatarFallback>ME</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-16">BIO</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96">
                        <p className="text-sm leading-6 whitespace-pre-line">{user.bio}</p>
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" className="w-16">NW</Button>
                  </div>
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-serif text-3xl font-bold text-black">{user.name}</h2>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                    <a href="/ispai-channel" className="ml-2">
                      <img 
                        src="/lovable-uploads/d7afdb11-1c93-4596-9b11-91cb70981610.png" 
                        alt="iSPAI Channel" 
                        className="w-16 h-16 hover:scale-110 transition-transform"
                      />
                    </a>
                  </div>
                  <p className="text-lg font-medium text-black mb-4">{user.role}</p>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/morgan"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={9.3} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MorganProfile;
